import React, { useState, useMemo } from 'react';
import { Input } from '../../../ui';
import { CoffeeShop } from '../hooks/useCoffeeShops';
import { ShopCard } from '../components';
import { useUserLocation, haversineKm } from '../hooks/useUserLocation';

interface ShopListViewProps {
    coffeeShops: CoffeeShop[];
    coffeeTypesCache: Record<string, { iconName: string; displayName: string }>;
    loading?: boolean;
}

type SortOption = 'rating' | 'name' | 'ratings-count' | 'distance';

const ShopListView: React.FC<ShopListViewProps> = ({
    coffeeShops,
    coffeeTypesCache,
    loading = false
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('rating');
    const [selectedCity, setSelectedCity] = useState<string>('all');
    const [includeUnrated, setIncludeUnrated] = useState(false);
    const { location } = useUserLocation(false);

    // shops the user actually cares about by default — only those with ratings
    const visibleShops = useMemo(
        () => (includeUnrated ? coffeeShops : coffeeShops.filter(s => s.totalRatings > 0)),
        [coffeeShops, includeUnrated]
    );

    // get unique cities (only from rated/visible shops so the dropdown stays consistent)
    const cities = useMemo(() => {
        return Array.from(new Set(visibleShops.map(shop => shop.city))).sort();
    }, [visibleShops]);

    // filter and sort shops
    const filteredShops = useMemo(() => {
        const items = visibleShops
            .filter(shop => {
                const matchesSearch =
                    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    shop.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    shop.city.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesCity = selectedCity === 'all' || shop.city === selectedCity;
                return matchesSearch && matchesCity;
            })
            .map(shop => {
                const distanceKm =
                    location && shop.coordinates?.lat && shop.coordinates?.lng
                        ? haversineKm(location, shop.coordinates)
                        : undefined;
                return { shop, distanceKm };
            });
        items.sort((a, b) => {
            switch (sortBy) {
                case 'rating':
                    return b.shop.averageRating - a.shop.averageRating;
                case 'name':
                    return a.shop.name.localeCompare(b.shop.name);
                case 'ratings-count':
                    return b.shop.totalRatings - a.shop.totalRatings;
                case 'distance': {
                    const aD = a.distanceKm ?? Infinity;
                    const bD = b.distanceKm ?? Infinity;
                    return aD - bD;
                }
                default:
                    return 0;
            }
        });
        return items;
    }, [visibleShops, searchQuery, sortBy, selectedCity, location]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-4" />
                <p className="text-muted-foreground">Loading coffee shops...</p>
            </div>
        );
    }

    const unratedCount = coffeeShops.length - coffeeShops.filter(s => s.totalRatings > 0).length;

    return (
        <div>
            {/* filter strip — flat, no box chrome */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                <Input
                    type="text"
                    placeholder="Search shops or drinks…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 sm:max-w-md"
                />
                <div className="flex gap-2 flex-wrap">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                        className="px-3 py-2 rounded-sm border-brutal border-ink bg-paper text-ink font-bold text-xs uppercase tracking-wider cursor-pointer"
                    >
                        <option value="rating">Sort: Rating</option>
                        <option value="name">Sort: Name</option>
                        <option value="ratings-count">Sort: # Ratings</option>
                        {location && <option value="distance">Sort: Distance</option>}
                    </select>
                    <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        className="px-3 py-2 rounded-sm border-brutal border-ink bg-paper text-ink font-bold text-xs uppercase tracking-wider cursor-pointer"
                    >
                        <option value="all">All Cities</option>
                        {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* meta row */}
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.12em] text-ink/60 px-1 pb-3">
                <span>
                    {filteredShops.length} {filteredShops.length === 1 ? 'shop' : 'shops'} · sorted by{' '}
                    {sortBy === 'ratings-count' ? '# ratings' : sortBy} ↓
                    {!includeUnrated && unratedCount > 0 && (
                        <span className="text-ink/40 normal-case tracking-normal">
                            {' '}· {unratedCount} unrated hidden
                        </span>
                    )}
                </span>
                {unratedCount > 0 && (
                    <button
                        type="button"
                        onClick={() => setIncludeUnrated(v => !v)}
                        className="text-ink/70 hover:text-ink underline underline-offset-2 normal-case font-medium tracking-normal"
                    >
                        {includeUnrated ? 'hide unrated' : 'show all'}
                    </button>
                )}
            </div>

            {/* empty state */}
            {filteredShops.length === 0 ? (
                <div className="py-12 text-center border-t border-ink/15">
                    <h3 className="text-lg font-black text-ink uppercase tracking-tight mb-1">No coffee shops</h3>
                    <p className="text-ink/60 text-sm">
                        {searchQuery || selectedCity !== 'all'
                            ? 'Try adjusting your search or filters.'
                            : 'No coffee shops rated yet — be the first.'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredShops.map(({ shop, distanceKm }) => (
                        <ShopCard
                            key={shop._id}
                            shop={shop}
                            coffeeTypesCache={coffeeTypesCache}
                            distanceKm={distanceKm}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ShopListView;
